import React, { useState } from 'react';
import Breadcrumb from "app/components/Breadcrumb";
import { Delete, Edit, Search, Replay } from "@mui/icons-material";
import useLoanType from "./hooks/useLoanType";
import LoanTypeEditorDialog from "./components/LoanTypeEditorDialog";
import ConfirmationDialog from "app/components/ConfirmationDialog";
import { themeShadows } from "app/components/MatxTheme/options/themeColors";
import {
    Box,
    Card,
    styled,
    TextField,
    InputAdornment,
    Button,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import useRoles from "app/hooks/useRoles";

const STATUSTYPES = {
    ACTIVE: 1,
    DEFAULT: 2,
    DELETED: 3,
};

const Status = styled("small")(({ theme, color }) => ({
    color: "#fff",
    padding: "2px 8px",
    overflow: "hidden",
    borderRadius: "300px",
    boxShadow: themeShadows[3],
    background: theme.palette[color].main
}));

export default function LoanType() {
    const [newLoanType, setNewLoanType] = useState({
        fieldid: '',
        description: '',
        type: ''
    });

    const getrolebyusername = useRoles();
    const isActionEnabled = getrolebyusername.includes('SA') || getrolebyusername.includes('Admin');

    const Container = styled("div")(({ theme }) => ({
        margin: "30px",
        [theme.breakpoints.down("sm")]: { margin: "16px" },
        "& .breadcrumb": {
            marginBottom: "0px",
            [theme.breakpoints.down("sm")]: { marginBottom: "16px" }
        }
    }));

    const handleAddNewUser = () => {
        setLoanType(null);
        setShouldOpenEditorDialog(true);
    };

    const {
        handleEditUser,
        handleDeleteUser,
        handleReactivateLoanType,
        shouldOpenEditorDialog,
        handleDialogClose,
        loantype,
        setShouldOpenEditorDialog,
        loanTypes,
        setLoanType,
        shouldOpenConfirmationDialog,
        handleConfirmationResponse,
        shouldOpenReactivateConfirmationDialog,
        handleReactivateConfirmationResponse
    } = useLoanType();

    const [searchQuery, setSearchQuery] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);

    const gridColumns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'description', headerName: 'Description', flex: 2 },
        {
            field: 'statusLUId', headerName: 'Status', flex: 1,
            renderCell: (params) => {
                const val = params.value;
                if (val === STATUSTYPES.ACTIVE) return <Status color="success">Active</Status>;
                if (val === STATUSTYPES.DEFAULT) return <Status color="primary">Default</Status>;
                return <Status color="error">Deleted</Status>;
            }
        },
        ...(isActionEnabled ? [{
            field: 'actions', headerName: 'Action', flex: 1, sortable: false,
            renderCell: (params) => {
                const lt = params.row;
                return (
                    <>
                        <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleEditUser(lt)(e); }} disabled={lt.statusLUId === STATUSTYPES.DEFAULT}>
                            <Edit />
                        </IconButton>
                        {lt.statusLUId === STATUSTYPES.DELETED ? (
                            <Tooltip title="Reactivate">
                                <IconButton color="success" onClick={() => handleReactivateLoanType(lt)}>
                                    <Replay />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <IconButton color="error" onClick={() => handleDeleteUser(lt)} disabled={lt.statusLUId === STATUSTYPES.DEFAULT}>
                                <Delete />
                            </IconButton>
                        )}
                    </>
                );
            }
        }] : [])
    ];

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleToggleShowDeleted = () => {
        setShowDeleted(prev => !prev);
    };

    const filteredLoanTypeList = loanTypes.filter(loantyperecord =>
        (showDeleted || loantyperecord.statusLUId !== STATUSTYPES.DELETED) &&
        (
            loantyperecord.code.toLowerCase().startsWith(String(searchQuery).toLowerCase()) ||
            loantyperecord.description.toLowerCase().startsWith(String(searchQuery).toLowerCase())
        )
    );

    const handleInputChange = (e) => {
        setNewLoanType({ ...newLoanType, [e.target.name]: e.target.value });
    };

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb routeSegments={[{ name: "Loan Type" }]} />
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    sx={{ mb: 2, mt: 2 }}
                    color="primary"
                    variant="contained"
                    onClick={handleAddNewUser}
                >
                    Add Loan Type
                </Button>

                <TextField
                    label="Search by Code or Description"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '300px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        sx: { height: '40px' }
                    }}
                />
            </Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <FormControlLabel
                    control={<Switch checked={showDeleted} onChange={handleToggleShowDeleted} />}
                    label="Show Deleted"
                />
            </Box>

            <Card sx={{ width: "100%", overflow: "auto" }} elevation={6}>
                <DataGrid
                    rows={filteredLoanTypeList}
                    columns={gridColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    autoHeight
                    disableSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        },
                    }}
                />
            </Card>

            {
                shouldOpenEditorDialog && (
                    <LoanTypeEditorDialog
                        open={shouldOpenEditorDialog}
                        handleClose={handleDialogClose}
                        loantype={loantype}
                    />
                )
            }

            {
                shouldOpenConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to delete this Loan Type?"
                    />
                )
            }

            {
                shouldOpenReactivateConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenReactivateConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleReactivateConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to reactivate this Loan Type?"
                    />
                )
            }
        </Container >

    );
}